
import { notification } from "antd";
import qs from "qs";
import store from "../../../redux/store";
import {
  enqueueGenericLog,
  resolveViewKey,
  getSessionId,
  getRoleNames,
  notifyAuthToken,
} from "services/UsageTrackingService";
import { redactSensitive } from "utils/tracking";

const TOKEN_PAYLOAD_KEY = "authorization";

// GETs são a maioria do tráfego (polling de dashboards) e o uso já é coberto por
// PAGE_VIEW; amostramos para reduzir carga no backend de logs e custo no Datadog.
// Mutações (POST/PUT/DELETE) são SEMPRE logadas (trilha de auditoria).
const GET_SAMPLE_RATE = 0.1;

// Leituras deveriam responder rápido (usuário percebe >30s como travamento);
// escritas/uploads (ex.: patches grandes) mantêm o timeout padrão da instância.
const GET_TIMEOUT_MS = 30000;
const DEFAULT_TIMEOUT_MS = 60000;

function selectUser(state) {
  return state.auth.user;
}

function redactUrl(url) {
  if (typeof url !== "string" || !url.includes("?")) return url;

  try {
    const [path, queryString] = url.split("?");
    const params = new URLSearchParams(queryString);
    const safeParams = redactSensitive(Object.fromEntries(params.entries()));
    return `${path}?${qs.stringify(safeParams)}`;
  } catch (_) {
    return url;
  }
}

export async function requestHandler(config) {
  const env = process.env.NODE_ENV;
  const user = selectUser(store.getState());
  const jwtToken = await user?.getIdToken();

  if (jwtToken) {
    // axios 1.x entrega config.headers como AxiosHeaders (usar .set); mantém
    // fallback para objeto simples (testes/mocks/versões antigas).
    if (config.headers && typeof config.headers.set === "function") {
      config.headers.set(TOKEN_PAYLOAD_KEY, "Bearer " + jwtToken);
    } else {
      config.headers = { ...(config.headers || {}), [TOKEN_PAYLOAD_KEY]: "Bearer " + jwtToken };
    }
    // Guarda o token para o flush de telemetria no fechamento da aba (keepalive).
    notifyAuthToken(jwtToken);
  }

  // axios 1.x: paramsSerializer é objeto { serialize } (a forma de função foi removida).
  config.paramsSerializer = {
    serialize: (params) => qs.stringify(params),
  };

  const method = (config.method || "get").toLowerCase();

  // Marca o início para medição de latência no interceptor de resposta.
  config.metadata = { ...(config.metadata || {}), startTime: Date.now() };

  if (method === "get" && (!config.timeout || config.timeout === DEFAULT_TIMEOUT_MS)) {
    config.timeout = GET_TIMEOUT_MS;
  }

  if (env !== "development") {
    // Rotas que não devem ter logs registrados
    const ignoredRoutes = ["/", "/apis", "/sendLog", "/sendGenericLog", "/json"];
    const isIgnoredRoute = ignoredRoutes.some(route => config.url.endsWith(route));

    const isMutation = method !== "get";
    // Retentativas automáticas não são novas ações do usuário; GETs são amostrados.
    const shouldLog = !config.__isRetry && (isMutation || Math.random() < GET_SAMPLE_RATE);

    if (!isIgnoredRoute && shouldLog) {
      // Captura os dados no momento da requisição
      const currentUser = selectUser(store.getState());
      const currentPageHref = window.location.href;
      const roleNames = getRoleNames();

      const logData = generateInfoLog({
        url: config.url,
        method,
        params: config.params,
        data: config.data,
        pageLink: currentPageHref,
        viewKey: resolveViewKey(window.location.pathname),
        role: roleNames.length ? roleNames.join(", ") : "unknown",
        sessionId: getSessionId() || undefined,
        sampled: !isMutation,
        sampleRate: isMutation ? 1 : GET_SAMPLE_RATE,
      });

      // Envio pela fila ÚNICA do UsageTrackingService (o endpoint de log não é
      // seguro para escrita concorrente; ver comentário lá).
      // Todo o projeto compartilha o mesmo `type` (DASHBOARD); o evento vai em `subtype`.
      enqueueGenericLog({
        gameId: "global_prod",
        logType: "DASHBOARD",
        userEmail: currentUser?.email || "unknown",
        customProperties: { subtype: "FRONTEND_ACTION", ...logData }
      });
    }
  }

  return config;
}

export function requestErrorHandler(error) {
  notification.error({
    message: "Ocorreu um erro ao processar a requisição",
  });
  Promise.reject(error);
}

// Gera o payload do log com as informações da requisição e da página
function generateInfoLog({
  url,
  method,
  params,
  data,
  pageLink,
  viewKey,
  role,
  sessionId,
  sampled,
  sampleRate,
}) {
  const reqExample = {
    get: "Solicitação de dados simples",
    post: "Adição de informações ou solicitação de dados complexos",
    put: "Modificação de dados",
    delete: "Deleção de dados"
  }
  let uploadedFileNames;

  // Se o data for uma instância de FormData, extrai o nome do(s) arquivo(s)
  if (data instanceof FormData) {
    const fileNames = [];
    for (let [_, value] of data.entries()) {
      if (value instanceof File) {
        fileNames.push(value.name);
      }
    }
    if (fileNames.length > 0) {
      uploadedFileNames = fileNames;
    }
  }

  // Payload de audit com valores sensíveis redigidos (cópia — não muta o body
  // real da requisição). FormData vira undefined; os arquivos vão em uploadedFileNames.
  const safeData = data instanceof FormData ? undefined : redactSensitive(data);
  const safeParams = redactSensitive(params);
  const safeUrl = redactUrl(url);

  return {
    // Facets comuns a todos os eventos de usabilidade no Datadog.
    source: "dashboard-frontend",
    env: process.env.NODE_ENV,
    role,
    viewKey,
    sessionId,
    sampled,
    sampleRate,
    // Telemetria leve, agregável (feature/cargo/sessão) e sem payload sensível.
    // Pensada para ser consumida amplamente; separável do audit no backend.
    telemetry: {
      application: "dashboard-frontend",
      viewKey,
      role,
      sessionId,
      action: reqExample[method],
      method,
      urlPath: safeUrl,
      pageLink,
      sampled,
      sampleRate,
    },
    generalData: {
      pageLink: pageLink,
      action: reqExample[method],
      dataSend: {
        infoSended: safeData,
        extraUrlInfo: safeParams,
        uploadedFileNames
      }
    },
    techData: {
      application: "dashboard-frontend",
      method: method,
      url: safeUrl,
      data: { ...safeData, uploadedFileNames },
      params: safeParams
    },
  }
};
